import { Card, CardContent, Container } from '@packages/shared';
import type { CmsDataJsonApi } from '@packages/cms-components/interfaces/jsonapi';
import { ComponentRecognizer } from '@packages/cms-components/src/components/ComponentRecognizer/ComponentRecognizer';
import { Checkmark32 } from '@packages/themes/icons';
import { useTheme } from '@mui/material';
import { Base64 } from 'js-base64';
import { renderToString } from 'react-dom/server';
import { SnippetTypeList } from '@packages/cms-components/interfaces/contentSnippet';
import { registerFlexibleComponents } from '@packages/cms-components/src/components/ComponentRecognizer/registerFlexibleComponents';

// register the dynamic components
registerFlexibleComponents();

export type ServiceLayerItemProps = {
  /** json data used to render banner */
  serviceLayerData: CmsDataJsonApi;
};

/**
 * This component displays Service Layer Content on Product Detailview Pages
 *
 * @param serviceLayerData Object contains data
 * @returns JSX.Element to be placed in PageLayout
 */
const ServiceLayerItem = ({ serviceLayerData }: ServiceLayerItemProps): JSX.Element => {
  const theme = useTheme();
  // Transform Icon to base64 encoded string
  // Step 1: need to transform the rendered icon component to a string
  const iconString = renderToString(<Checkmark32 style={{ fill: theme.palette.success.main }} />);
  // Step 2: encode string to base64
  // the result can now be displayed as a background image using a style statement like:
  // `transparent url('data:image/svg+xml;base64,${iconStringEncoded}') center center no-repeat`
  const iconStringEncoded = Base64.encode(iconString);

  return (
    <Container
      maxWidth="md"
      sx={{
        padding: 2,
        fontSize: 'body2.fontSize',
        'ul, ol': {
          paddingLeft: 0,
          li: {
            '&.type-1, &.type-2, &.type-3': {
              listStyleType: 'none',
            },
            '&.type-1': {
              marginLeft: '-1rem',
              '&:before': {
                content: '" "',
                background: `transparent url('data:image/svg+xml;base64,${iconStringEncoded}') center center no-repeat`,
                flexShrink: 0,
                flexBasis: '1rem',
                width: { sm: '1.25rem', md: '1.5rem' },
                height: { sm: '0.94rem', md: '1.5rem' },
                marginRight: 1,
                display: 'inline-block',
                verticalAlign: 'middle',
              },
            },
            '&.type-3': {
              paddingLeft: { sm: 2, md: 1 },
            },
            ':last-child': {
              paddingBottom: 1,
            },
          },
        },
      }}
    >
      <Card sx={{ minWidth: 275 }}>
        <CardContent>
          {serviceLayerData.data.map(
            (item, index): JSX.Element | false =>
              item.type === SnippetTypeList.serviceLayerDv &&
              item.field_paragraph && (
                // The unique key is important from a performance point of view so that React doesn't have to render everything.
                // In this case it doesn't matter if only index or key is passed. The list is as it is and done.
                // eslint-disable-next-line react/no-array-index-key
                <ComponentRecognizer key={index} item={item.field_paragraph} />
              ),
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

// eslint-disable-next-line import/no-default-export
export default ServiceLayerItem;
